import { useEffect, useRef, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { Toaster, toast } from "sonner";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import MentionsLegales from "@/pages/MentionsLegales";
import Bienvenue from "@/pages/Bienvenue";
import {
  Send,
  Copy,
  Download,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Mail,
  FileText,
  Lock,
  Heart,
  KeyRound,
  Building2,
  Landmark,
  HeartHandshake,
  User,
  UserCog,
  Vault,
} from "lucide-react";
import { canGenerateLetter, consumeLetter, getLetterRemaining, DAILY_LIMIT as LETTER_DAILY_LIMIT } from "@/lib/letterLimit";
import { getProfile, isProfileComplete, buildProfilePrefix } from "@/lib/profile";
import { saveAsReady } from "@/lib/lettersStore";
import CoffreModal from "@/components/CoffreModal";
import ProfileModal from "@/components/ProfileModal";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function generateSessionId() {
  return (
    "s_" +
    Date.now().toString(36) +
    "_" +
    Math.random().toString(36).slice(2, 10)
  );
}

function Logo({ size = 38 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Agent IA Administratif"
    >
      <defs>
        <linearGradient id="goldGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F3E5AB" />
          <stop offset="55%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#8C7220" />
        </linearGradient>
      </defs>
      <rect
        x="3"
        y="3"
        width="42"
        height="42"
        rx="3"
        fill="#0A0A0A"
        stroke="url(#goldGrad)"
        strokeWidth="1.2"
      />
      <rect
        x="6"
        y="6"
        width="36"
        height="36"
        rx="2"
        fill="none"
        stroke="rgba(212,175,55,0.25)"
        strokeWidth="0.6"
      />
      <path
        d="M14 36 L24 11 L34 36 M18.5 28 L29.5 28"
        stroke="url(#goldGrad)"
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="24" cy="20" r="1.5" fill="#F3E5AB" />
      <circle cx="14" cy="14" r="0.9" fill="#D4AF37" />
      <circle cx="34" cy="14" r="0.9" fill="#D4AF37" />
      <line x1="14" y1="14" x2="24" y2="20" stroke="#D4AF37" strokeWidth="0.4" />
      <line x1="34" y1="14" x2="24" y2="20" stroke="#D4AF37" strokeWidth="0.4" />
    </svg>
  );
}

function Header() {
  return (
    <header
      data-testid="app-header"
      className="w-full border-b border-white/5 backdrop-blur-md bg-black/30 sticky top-0 z-30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Logo size={38} />
            <div className="gold-dot absolute -right-1 -top-1" />
          </div>
          <div className="leading-tight">
            <div className="font-serif-display text-xl tracking-tight">
              <span style={{ color: "#D4AF37" }}>Zerux</span> · Agent Administratif <span style={{ color: "#D4AF37" }}>IA</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Rédaction conversationnelle · officielle
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-6 text-xs text-zinc-400">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
            <span>RGPD — aucune donnée sur nos serveurs</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock size={14} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
            <span>Session anonyme</span>
          </div>
        </div>
      </div>
    </header>
  );
}

// === Lien de paiement Stripe Zerux ===
const PAYHIP_LINK = "https://buy.stripe.com/5kQ6oGdgz8dzcvn15y8Vi06";

function SubscribeButton({ label = "Acheter l'accès", variant = "solid", href = PAYHIP_LINK, testId = "payhip-buy-button" }) {
  const base = "h-10 px-4 rounded-sm flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium transition-colors";
  const cls = variant === "solid" ? `btn-gold-solid ${base}` : `btn-gold ${base}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={cls}
    >
      <Heart size={14} strokeWidth={1.8} />
      <span>{label}</span>
    </a>
  );
}

function Footer() {
  return (
    <footer
      data-testid="app-footer"
      className="w-full border-t border-white/5 mt-12"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 text-[11px] text-zinc-500 leading-relaxed flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-center sm:text-left">
          <span className="font-serif-display text-zinc-300">HELP&rsquo;<span style={{ color: "#D4AF37" }}>ADN</span></span>
          <span className="mx-2 text-zinc-700">·</span>
          <span>© {new Date().getFullYear()} — Tous droits réservés</span>
        </div>
        <Link
          to="/mentions-legales"
          data-testid="footer-mentions-link"
          className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors gold-underline text-[11px] uppercase tracking-[0.2em]"
        >
          Mentions légales
        </Link>
      </div>
    </footer>
  );
}

function TypingDots() {
  return (
    <div className="inline-flex items-center gap-1.5" aria-label="Génération en cours">
      <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
      <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
      <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
    </div>
  );
}

function ChatPane({
  messages,
  input,
  setInput,
  onSend,
  onClear,
  isLoading,
}) {
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = "auto";
      taRef.current.style.height = Math.min(taRef.current.scrollHeight, 180) + "px";
    }
  }, [input]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <section
      data-testid="chat-pane"
      className="flex flex-col h-[78vh] md:h-[80vh] rounded-md border border-white/5 bg-[#0B0B0B]/60 backdrop-blur-sm overflow-hidden"
    >
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail size={16} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
          <span className="font-serif-display text-lg">Conversation</span>
        </div>
        <button
          data-testid="clear-chat-button"
          onClick={onClear}
          className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-[#D4AF37] flex items-center gap-1.5 transition-colors"
        >
          <RotateCcw size={12} strokeWidth={1.5} />
          Nouvelle session
        </button>
      </div>

      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className="flex-1 overflow-y-auto px-6 py-6 space-y-5"
      >
        {messages.length === 0 && (
          <div className="fade-up">
            <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-600 mb-3">
              Bienvenue
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mb-4">
              Rédigez une lettre administrative
              <br />
              <span className="shimmer-text">en une phrase.</span>
            </h2>
            <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
              Indiquez l&rsquo;organisme, l&rsquo;objet et vos informations
              (nom, prénom, adresse, dossier, dates…). La lettre apparaît à
              droite, prête à être copiée ou téléchargée.
            </p>

            <div className="mt-5 rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1.5">
                Tous types de lettres
              </div>
              <p className="text-xs text-zinc-300 leading-relaxed">
                CAF · CPAM · MSA · URSSAF · Impôts · Trésor public · France
                Travail · CARSAT · CNAV · MDPH · Préfecture ·
                bailleurs · banques · assurances · écoles · employeurs ·
                entreprises · entrepreneurs · auto‑entrepreneurs ·
                micro‑entreprises · associations loi 1901 · collectivités · et
                tout autre organisme public ou privé.
              </p>
            </div>

            <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Quelques exemples
            </div>
            <div className="grid gap-2">
              {[
                "Lettre d'un particulier à la CAF pour signaler un changement d'adresse — Marie Lefèvre, 12 rue des Lilas, 59000 Lille, n° allocataire 1234567",
                "Lettre du Maire de Carvin à un administré en réponse à sa réclamation concernant la voirie rue des Acacias",
                "Lettre du directeur d'école aux parents pour annoncer la réunion de rentrée du 5 septembre",
                "Lettre d'un auto‑entrepreneur à l'URSSAF pour demander un délai de paiement — SIRET 12345678900012, motif difficultés financières",
                "Lettre du président d'une association loi 1901 à la mairie pour demande de subvention annuelle 2026 — association « Les Amis du Quartier »",
                "Lettre d'une entreprise à un client pour relance de facture impayée n°2025‑018, montant 1 250 €, échéance dépassée",
              ].map((s, i) => (
                <button
                  key={i}
                  data-testid={`suggestion-${i}`}
                  onClick={() => setInput(s)}
                  className="text-left text-xs text-zinc-400 hover:text-[#D4AF37] border border-white/5 hover:border-[#D4AF37]/40 rounded-sm px-3 py-2.5 transition-colors"
                >
                  <span className="text-[#D4AF37] mr-2">›</span>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className="fade-up">
            {m.role === "user" ? (
              <div className="flex justify-end">
                <div
                  data-testid={`user-msg-${i}`}
                  className="max-w-[85%] rounded-sm px-4 py-3 text-sm leading-relaxed"
                  style={{
                    background: "rgba(212,175,55,0.08)",
                    border: "1px solid rgba(212,175,55,0.18)",
                    color: "#F3E5AB",
                  }}
                >
                  {m.content}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div
                  className="w-7 h-7 shrink-0 rounded-sm flex items-center justify-center"
                  style={{
                    background: "#0F0F0F",
                    border: "1px solid rgba(212,175,55,0.35)",
                  }}
                >
                  <Sparkles size={13} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
                </div>
                <div
                  data-testid={`bot-msg-${i}`}
                  className="max-w-[85%] text-sm text-zinc-300 leading-relaxed"
                >
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
                    Zerux
                  </div>
                  {m.content}
                </div>
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex items-start gap-3 fade-up">
            <div
              className="w-7 h-7 shrink-0 rounded-sm flex items-center justify-center gold-pulse"
              style={{
                background: "#0F0F0F",
                border: "1px solid rgba(212,175,55,0.5)",
              }}
            >
              <Sparkles size={13} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
            </div>
            <div className="text-sm text-zinc-400 pt-1">
              <span className="mr-3">Rédaction de votre lettre</span>
              <TypingDots />
            </div>
          </div>
        )}
      </div>

      <div className="px-4 pb-4 pt-3 border-t border-[#D4AF37]/30 bg-gradient-to-t from-[#D4AF37]/5 to-transparent">
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
            <span className="gold-dot" />
            <span>Votre demande</span>
          </div>
          <span className="text-[10px] text-zinc-600">Entrée pour envoyer</span>
        </div>
        <div className="glass-input rounded-md p-3 flex items-end gap-3" style={{borderBottomWidth: '2px', borderBottomColor: '#D4AF37'}}>
          <textarea
            ref={taRef}
            data-testid="chat-input-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ex. : Lettre à la CPAM pour demande de remboursement — Marie Martin, 22 rue Pasteur 75015 Paris, dossier n°987654…"
            rows={1}
            disabled={isLoading}
            autoFocus
            className="flex-1 bg-transparent outline-none text-sm text-zinc-100 placeholder-zinc-500 resize-none leading-relaxed font-light"
            style={{ maxHeight: 180 }}
          />
          <button
            data-testid="chat-submit-button"
            onClick={onSend}
            disabled={isLoading || !input.trim()}
            className="btn-gold-solid h-10 px-4 rounded-sm flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium shrink-0"
          >
            <Send size={14} strokeWidth={1.8} />
            <span className="hidden sm:inline">Générer</span>
          </button>
        </div>
        <div className="mt-2 text-[10px] text-zinc-600 px-1 flex items-center gap-2">
          <ShieldCheck size={11} strokeWidth={1.5} />
          <span>
            Aucune donnée n&rsquo;est conservée — votre saisie disparaît avec la
            session.
          </span>
        </div>
      </div>
    </section>
  );
}

function DocumentPane({ letter, isLoading, onCopy, onDownload }) {
  return (
    <section
      data-testid="document-pane"
      className="flex flex-col h-[78vh] md:h-[80vh] rounded-md border border-white/5 bg-[#0B0B0B]/60 backdrop-blur-sm overflow-hidden"
    >
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={16} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
          <span className="font-serif-display text-lg">Lettre générée</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="copy-letter-button"
            onClick={onCopy}
            disabled={!letter || isLoading}
            className="btn-gold h-9 px-3 rounded-sm text-[11px] uppercase tracking-[0.2em] flex items-center gap-1.5"
          >
            <Copy size={12} strokeWidth={1.8} />
            Copier
          </button>
          <button
            data-testid="download-pdf-button"
            onClick={onDownload}
            disabled={!letter || isLoading}
            className="btn-gold-solid h-9 px-3 rounded-sm text-[11px] uppercase tracking-[0.2em] flex items-center gap-1.5"
          >
            <Download size={12} strokeWidth={1.8} />
            PDF
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 md:p-8" data-testid="document-scroll">
        {!letter && !isLoading && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-sm">
              <div
                className="mx-auto w-14 h-14 rounded-sm flex items-center justify-center mb-5"
                style={{
                  background: "#0F0F0F",
                  border: "1px solid rgba(212,175,55,0.25)",
                }}
              >
                <FileText size={22} strokeWidth={1.2} style={{ color: "#D4AF37" }} />
              </div>
              <h3 className="font-serif-display text-2xl mb-2">
                Aperçu de la lettre
              </h3>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Votre lettre administrative apparaîtra ici, mise en forme sur un
                papier net, prête à être copiée ou exportée en PDF.
              </p>
            </div>
          </div>
        )}

        {isLoading && !letter && (
          <div className="h-full flex flex-col items-center justify-center gap-4">
            <div
              className="w-14 h-14 rounded-sm flex items-center justify-center gold-pulse"
              style={{
                background: "#0F0F0F",
                border: "1px solid rgba(212,175,55,0.5)",
              }}
            >
              <Sparkles size={22} strokeWidth={1.2} style={{ color: "#D4AF37" }} />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">
              Rédaction en cours
            </div>
            <TypingDots />
          </div>
        )}

        {letter && (
          <article
            data-testid="generated-document-preview"
            className="paper rounded-sm mx-auto max-w-[680px] px-8 md:px-12 py-12 md:py-14 fade-up"
          >
            <div className="paper-text">{letter}</div>
          </article>
        )}
      </div>
    </section>
  );
}

const LS_COUNT = "aia_free_count";
const LS_UNLOCKED = "aia_unlocked";

function BlockedScreen({ onUnlock }) {
  const [pwd, setPwd] = useState("");
  const [checking, setChecking] = useState(false);

  const handleValidate = async () => {
    const password = pwd.trim();
    if (!password || checking) return;
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/unlock`, { password });
      if (data.valid) {
        toast.success("Accès débloqué — bienvenue !");
        onUnlock();
      } else {
        toast.error("Mot de passe incorrect.");
      }
    } catch {
      toast.error("Erreur de vérification. Réessayez.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      data-testid="blocked-screen"
      className="min-h-[60vh] flex items-center justify-center fade-up"
    >
      <div
        className="max-w-xl w-full mx-auto p-8 md:p-12 rounded-md text-center"
        style={{
          background: "#0B0B0B",
          border: "1px solid rgba(212,175,55,0.25)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
        }}
      >
        <div
          className="mx-auto w-20 h-20 rounded-full overflow-hidden mb-5 gold-pulse flex items-center justify-center"
          style={{
            border: "2px solid rgba(212,175,55,0.55)",
            boxShadow: "0 0 30px rgba(212,175,55,0.18)",
            background: "radial-gradient(circle at 30% 30%, #1a1a1a 0%, #050505 100%)",
          }}
          data-testid="agent-avatar"
        >
          <svg
            viewBox="0 0 100 100"
            className="w-14 h-14"
            aria-label="Zerux — monogramme"
          >
            <defs>
              <linearGradient id="zMonoGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F3E5AB" />
                <stop offset="50%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#8B6F1E" />
              </linearGradient>
            </defs>
            <text
              x="50"
              y="70"
              textAnchor="middle"
              fontFamily="Cormorant Garamond, serif"
              fontSize="70"
              fontWeight="500"
              fontStyle="italic"
              fill="url(#zMonoGold)"
              letterSpacing="-2"
            >
              Z
            </text>
          </svg>
        </div>

        <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-2">
          Zerux · Bienvenue
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mb-4">
          Bonjour et <span className="shimmer-text">bienvenue.</span>
        </h2>
        <p className="text-sm text-zinc-300 leading-relaxed max-w-lg mx-auto mb-7">
          Fini le stress du courrier officiel. Zerux comprend l&rsquo;administration française mieux que personne&nbsp;:
          <strong className="text-zinc-100"> il rédige la bonne lettre, au bon ton — instantanément.</strong>
          Une équipe entière de secrétariat, dans votre poche.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-xl mx-auto mb-6" data-testid="audience-grid">
          {[
            { icon: Building2, label: "Entreprises", sub: "TPE · PME · groupes" },
            { icon: HeartHandshake, label: "Associations", sub: "Loi 1901 · fondations" },
            { icon: Landmark, label: "Services publics", sub: "Collectivités · agences" },
            { icon: User, label: "Particuliers", sub: "Familles · seniors" },
          ].map(({ icon: Icon, label, sub }, i) => (
            <div
              key={i}
              data-testid={`audience-card-${i}`}
              className="rounded-sm px-3 py-4 text-left"
              style={{
                background: "rgba(212,175,55,0.04)",
                border: "1px solid rgba(212,175,55,0.15)",
              }}
            >
              <Icon size={18} strokeWidth={1.4} style={{ color: "#D4AF37" }} />
              <div className="mt-2 text-[11px] uppercase tracking-[0.15em] text-zinc-100 font-medium">
                {label}
              </div>
              <div className="mt-0.5 text-[10px] text-zinc-500 leading-snug">
                {sub}
              </div>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed max-w-lg mx-auto mb-6 italic">
          URSSAF · DGFiP · prud&rsquo;hommes · bailleurs · fournisseurs · CAF ·
          subvention · résiliation · réclamation · recours · attestation…
        </p>

        {/* --- Vidéo démo --- */}
        <div className="my-10 max-w-2xl mx-auto" data-testid="demo-video">
          <div className="text-center mb-4">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-1">Aperçu</div>
            <div className="text-[13px] text-zinc-300">Zerux en action</div>
          </div>
          <div
            className="rounded-md overflow-hidden border border-[#D4AF37]/25 bg-black"
            style={{ boxShadow: "0 20px 60px -20px rgba(212,175,55,0.18)" }}
          >
            <video
              src="/zerux-demo.mov"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-auto block"
              data-testid="demo-video-player"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 max-w-2xl mx-auto mb-7 text-left" data-testid="tool-features">
          {[
            "Rédaction de lettre en 10 secondes",
            "Analyse d'un courrier reçu (photo ou PDF)",
            "Réponse générée automatiquement",
            "Coordonnées enregistrées une seule fois",
            "Coffre organisé — Documents · Prête · Stockage",
            "Auto-nommage intelligent des lettres",
            "Téléchargement PDF individuel ou ZIP global",
            "100 % RGPD — stockage local, rien sur nos serveurs",
            "Format administratif français conforme",
          ].map((f, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-[11px] text-zinc-300 leading-snug"
              data-testid={`feature-${i}`}
            >
              <span className="mt-0.5" style={{ color: "#D4AF37" }}>✓</span>
              <span>{f}</span>
            </div>
          ))}
        </div>

        {/* --- Témoignages --- */}
        <div className="my-8 max-w-3xl mx-auto" data-testid="testimonials">
          <div className="text-center mb-5">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-1">Ils utilisent Zerux</div>
            <div className="flex items-center justify-center gap-0.5 text-[#D4AF37]">
              {[0,1,2,3,4].map(i => <span key={i} className="text-sm">★</span>)}
              <span className="text-[11px] text-zinc-400 ml-2">4,9 / 5</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3 text-left">
            {[
              {
                initials: "SM",
                name: "Sophie M.",
                role: "DRH — PME 42 salariés",
                text: "Nous gérons 15 à 20 courriers URSSAF et prud’hommes par mois. Zerux nous fait gagner 6 h par semaine, minimum. La qualité de rédaction est indistinguable de ce que faisait notre juriste.",
              },
              {
                initials: "TL",
                name: "Thomas L.",
                role: "Auto-entrepreneur",
                text: "J’ai récupéré un trop-perçu de 480 € à la CAF grâce à une lettre générée en 40 secondes. Un écrivain public m’aurait facturé 60 €. Zerux s’est amorti sur une seule utilisation.",
              },
              {
                initials: "AC",
                name: "Amélie C.",
                role: "Présidente d’association Loi 1901",
                text: "Les demandes de subvention à la mairie et à la Région étaient une corvée. Maintenant j’upload le formulaire reçu, Zerux rédige la réponse, je l’envoie. Redoutable.",
              },
            ].map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="rounded-md p-4 flex flex-col"
                style={{
                  background: "rgba(212,175,55,0.03)",
                  border: "1px solid rgba(212,175,55,0.15)",
                }}
              >
                <div className="flex items-center gap-2 mb-2 text-[#D4AF37]">
                  {[0,1,2,3,4].map(s => <span key={s} className="text-[11px]">★</span>)}
                </div>
                <p className="text-[12px] text-zinc-300 leading-relaxed italic mb-3 flex-1">
                  « {t.text} »
                </p>
                <div className="flex items-center gap-2.5 pt-2 border-t border-white/5">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0"
                    style={{
                      background: "rgba(212,175,55,0.15)",
                      border: "1px solid rgba(212,175,55,0.4)",
                      color: "#F3E5AB",
                    }}
                  >
                    {t.initials}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] text-zinc-100 truncate">{t.name}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
          <div className="flex-1 h-px bg-[#D4AF37]/20" />
          <span>Accès protégé</span>
          <div className="flex-1 h-px bg-[#D4AF37]/20" />
        </div>

        {/* ---- Login block ---- */}
        <div className="max-w-md mx-auto mb-4 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3">
            Vous avez déjà un mot de passe&nbsp;?
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="glass-input rounded-md px-3 flex items-center gap-2 flex-1" style={{borderBottomWidth:'2px',borderBottomColor:'#D4AF37'}}>
              <KeyRound size={14} strokeWidth={1.6} style={{ color: "#D4AF37" }} />
              <input
                data-testid="password-input"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleValidate()}
                placeholder="Saisir votre mot de passe"
                autoFocus
                className="flex-1 bg-transparent outline-none text-sm text-zinc-100 placeholder-zinc-500 py-2"
              />
            </div>
            <button
              data-testid="password-validate-button"
              onClick={handleValidate}
              disabled={checking || !pwd.trim()}
              className="btn-gold-solid h-10 px-5 rounded-sm text-xs uppercase tracking-[0.2em] font-medium"
            >
              {checking ? "..." : "Valider"}
            </button>
          </div>
        </div>

        {/* ---- OR divider ---- */}
        <div className="my-8 flex items-center gap-4 max-w-md mx-auto text-[9px] uppercase tracking-[0.4em] text-zinc-500">
          <div className="flex-1 h-px bg-white/10" />
          <span>Ou découvrez nos offres</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* ---- Pricing cards ---- */}
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-6 text-left" data-testid="pricing-cards">
          {/* Card Particulier */}
          <div className="relative border border-[#D4AF37]/40 bg-gradient-to-b from-[#D4AF37]/[0.06] to-transparent rounded-md p-6 flex flex-col" data-testid="pricing-card-solo">
            <div className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Recommandé</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Formule Particulier</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-3xl font-serif-display text-zinc-100">99,90&nbsp;€</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">TTC</span>
            </div>
            <div className="text-[11px] text-zinc-400 mb-4">Achat unique &middot; accès à vie</div>
            <ul className="text-[12px] text-zinc-300 space-y-1.5 mb-5 flex-1">
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> 4 lettres rédigées / jour</li>
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> 4 analyses de courrier reçu / jour</li>
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> Coffre organisé + export PDF &amp; ZIP</li>
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> Coordonnées mémorisées automatiquement</li>
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> 100 % RGPD — stockage 100 % local (navigateur)</li>
            </ul>
            <a
              data-testid="payhip-buy-button"
              href={PAYHIP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold-solid inline-flex justify-center items-center gap-2 h-11 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold w-full"
            >
              <KeyRound size={14} strokeWidth={1.8} />
              Acheter l&rsquo;accès
            </a>
          </div>

          {/* Card Business */}
          <div className="relative border border-white/10 bg-white/[0.02] rounded-md p-6 flex flex-col" data-testid="pricing-card-business">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">Formule Business</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mr-1">à partir de</span>
              <span className="text-3xl font-serif-display text-zinc-100">149&nbsp;€</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">/ mois HT</span>
            </div>
            <div className="text-[11px] text-zinc-400 mb-4">Entreprises &middot; associations &middot; cabinets</div>
            <ul className="text-[12px] text-zinc-300 space-y-1.5 mb-5 flex-1">
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> Volume illimité de lettres</li>
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> Comptes multi‑utilisateurs</li>
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> Facture professionnelle (SIRET)</li>
              <li className="flex gap-2"><span style={{color:'#D4AF37'}}>✓</span> Support prioritaire dédié</li>
            </ul>
            <a
              data-testid="business-contact-button"
              href="mailto:contact@helpadn.fr?subject=Demande%20formule%20Zerux%20Business&body=Bonjour%2C%0A%0AJe%20repr%C3%A9sente%20%5Bnom%20de%20l%27entreprise%2Fassociation%2Fcabinet%5D%20et%20souhaite%20une%20formule%20Zerux%20Business%20(volume%20%C3%A9lev%C3%A9%2C%20multi-utilisateurs%2C%20facture%20pro).%0A%0ANombre%20d%27utilisateurs%20estim%C3%A9%20%3A%20%0AVolume%20mensuel%20estim%C3%A9%20%3A%20%0ASecteur%20d%27activit%C3%A9%20%3A%20%0A%0AMerci%20de%20me%20recontacter%20pour%20un%20devis.%0A%0ACordialement%2C"
              className="btn-gold inline-flex justify-center items-center gap-2 h-11 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold w-full"
            >
              <Building2 size={14} strokeWidth={1.8} />
              Demander un devis personnalisé
            </a>
          </div>
        </div>

        {/* ---- Contact fallback ---- */}
        <div className="text-center mb-2">
          <a
            data-testid="contact-helpadn-mail"
            href="mailto:contact@helpadn.fr?subject=Question%20Zerux&body=Bonjour%2C%0A%0A"
            className="text-[11px] text-zinc-400 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5"
          >
            <Mail size={11} strokeWidth={1.8} />
            Une question&nbsp;? contact@helpadn.fr
          </a>
        </div>

        <p className="mt-4 text-[10px] text-zinc-600 text-center">
          Aucune donnée transmise à nos serveurs — votre mot de passe est uniquement vérifié, jamais conservé.
        </p>
      </div>
    </div>
  );
}

function Home() {
  const [sessionId, setSessionId] = useState(generateSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [letter, setLetter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [coffreOpen, setCoffreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileComplete, setProfileComplete] = useState(isProfileComplete());
  const [letterRemaining, setLetterRemaining] = useState(LETTER_DAILY_LIMIT);

  useEffect(() => {
    if (unlocked) {
      setLetterRemaining(getLetterRemaining());
      setProfileComplete(isProfileComplete());
    }
  }, [unlocked]);

  const refreshProfileState = () => setProfileComplete(isProfileComplete());

  const isBlocked = !unlocked;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    if (!unlocked) {
      toast.error("Accès protégé. Entrez votre mot de passe.");
      return;
    }

    if (!canGenerateLetter()) {
      toast.error(
        `Limite quotidienne atteinte (${LETTER_DAILY_LIMIT} lettres/jour). Réessayez demain à 00 h 00.`
      );
      return;
    }

    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setIsLoading(true);

    // Injecte automatiquement les coordonnées de l'utilisateur (localStorage)
    // pour que la lettre soit générée sans [placeholders]
    const prefix = buildProfilePrefix();
    const messageForAI = prefix
      ? `${prefix}\nDemande de l'utilisateur :\n${text}`
      : text;

    try {
      const { data } = await axios.post(`${API}/generate`, {
        message: messageForAI,
        session_id: sessionId,
      });
      const generated = (data.letter || "").trim();
      setLetter(generated);
      // Ajoute automatiquement la lettre dans la section "Lettre prête" du Coffre
      if (generated) saveAsReady(generated);
      consumeLetter();
      setLetterRemaining(getLetterRemaining());
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          content:
            "Votre lettre est prête à droite. Retrouvez-la dans Mon Coffre — section « Lettre prête » — où vous pourrez la valider et l'archiver.",
        },
      ]);
      if (data.session_id) setSessionId(data.session_id);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.";
      toast.error(msg);
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          content: "Une erreur est survenue lors de la génération. Veuillez reformuler ou réessayer.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([]);
    setLetter("");
    setInput("");
    setSessionId(generateSessionId());
    toast.success("Session effacée — aucune donnée conservée.");
  };

  const handleCopy = async () => {
    if (!letter) return;
    try {
      await navigator.clipboard.writeText(letter);
      toast.success("Lettre copiée dans le presse-papiers.");
    } catch {
      toast.error("Impossible de copier la lettre.");
    }
  };

  const handleDownload = () => {
    if (!letter) return;
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const marginX = 56;
      const marginTop = 72;
      const maxWidth = 595.28 - marginX * 2;
      doc.setFont("times", "normal");
      doc.setFontSize(11);
      doc.setTextColor(20, 20, 20);

      const lines = doc.splitTextToSize(letter, maxWidth);
      const pageHeight = 841.89;
      const lineHeight = 15;
      let y = marginTop;

      lines.forEach((ln) => {
        if (y + lineHeight > pageHeight - 60) {
          doc.addPage();
          y = marginTop;
        }
        doc.text(ln, marginX, y);
        y += lineHeight;
      });

      // Footer line
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        "Généré via HELP'ADN — Aucune donnée conservée.",
        marginX,
        pageHeight - 30
      );

      doc.save(`lettre-helpadn-${Date.now()}.pdf`);
      toast.success("PDF téléchargé.");
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0F0F0F",
            color: "#F3E5AB",
            border: "1px solid rgba(212,175,55,0.3)",
          },
        }}
      />
      <Header />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-10 py-8 md:py-10">
        <div className="mb-7 md:mb-10 flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3">
              Zerux · Agent Administratif IA
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Vos courriers administratifs,
              <br />
              <span className="shimmer-text">réglés en 10 secondes.</span>
            </h1>
            <p className="text-sm text-zinc-400 mt-4 max-w-xl leading-relaxed">
              <strong className="text-zinc-100">Entreprises, cabinets, associations, collectivités</strong>&nbsp;: arrêtez de perdre des heures à rédiger des courriers URSSAF, DGFiP, CAF, prud&rsquo;hommes, bailleurs, fournisseurs.
              Zerux <strong className="text-zinc-100">rédige la lettre exacte en 10 secondes</strong>, avec le style administratif attendu.
              Un courrier reçu à traiter&nbsp;? Uploadez-le, la <strong className="text-zinc-100">réponse est prête à envoyer</strong>.
              Coffre organisé, export PDF ou ZIP, facture professionnelle disponible.
              <br /><br />
              <span className="text-[#F3E5AB]">Le coût d&rsquo;une seule prestation de secrétariat externe — pour un usage illimité.</span>
              <br /><br />
              <span className="text-[12px] text-zinc-300">
                ⭐️ <strong className="text-zinc-100">Et pour les particuliers</strong> qui veulent gagner du temps sur leurs démarches (CAF, impôts, bailleur, employeur…) — Zerux fonctionne aussi, avec la même précision, dès 99,90&nbsp;€.
              </span>
            </p>
            {unlocked && (
              <div
                className="mt-4 text-[12px] text-zinc-500 leading-relaxed max-w-xl"
                data-testid="coffre-hint"
              >
                Deux façons d&rsquo;utiliser Zerux&nbsp;: <strong className="text-zinc-300">écrire une lettre</strong> à partir de votre demande, ou <strong className="text-zinc-300">analyser un courrier reçu</strong> pour obtenir la réponse adaptée.
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap justify-end">
            {unlocked && (
              <>
                <div
                  data-testid="letter-quota"
                  className="h-9 px-3 rounded-sm text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5"
                  style={{
                    background: "rgba(212,175,55,0.04)",
                    border: "1px solid rgba(212,175,55,0.15)",
                    color: "#F3E5AB",
                  }}
                  title="Lettres générées aujourd'hui — remise à zéro à minuit"
                >
                  <span style={{ color: "#D4AF37" }}>{LETTER_DAILY_LIMIT - letterRemaining} / {LETTER_DAILY_LIMIT}</span>
                  <span className="text-zinc-500 normal-case tracking-normal">lettres · jour</span>
                </div>
                <button
                  data-testid="open-coffre-button"
                  onClick={() => setCoffreOpen(true)}
                  className="btn-gold h-9 px-3 rounded-sm text-[11px] uppercase tracking-[0.2em] flex items-center gap-1.5"
                >
                  <Vault size={14} strokeWidth={1.6} />
                  Mon coffre
                </button>
              </>
            )}
            <div className="hidden md:flex items-center gap-3">
              <div className="gold-dot" />
              {unlocked ? (
                <span><span style={{color:"#D4AF37"}}>Accès actif</span> · {letterRemaining} lettre{letterRemaining > 1 ? "s" : ""} restante{letterRemaining > 1 ? "s" : ""} aujourd&rsquo;hui</span>
              ) : (
                <span><span style={{color:"#D4AF37"}}>Accès protégé</span> · mot de passe requis</span>
              )}
            </div>
          </div>
        </div>

        {isBlocked ? (
          <BlockedScreen onUnlock={() => setUnlocked(true)} />
        ) : (
        <>
          {/* ---- Profile CTA banner ---- */}
          {!profileComplete && (
            <div
              data-testid="profile-cta-banner"
              className="mb-4 rounded-md px-4 py-3 flex items-center gap-3 flex-wrap"
              style={{
                background: "linear-gradient(to right, rgba(212,175,55,0.10), rgba(212,175,55,0.02))",
                border: "1px solid rgba(212,175,55,0.35)",
              }}
            >
              <UserCog size={18} strokeWidth={1.6} style={{ color: "#D4AF37" }} className="shrink-0" />
              <div className="flex-1 min-w-0 text-[12px] text-zinc-300">
                <strong className="text-zinc-100">Renseignez vos coordonnées une seule fois</strong>
                <span className="text-zinc-400"> — Zerux les insère automatiquement dans toutes vos lettres. Fini les crochets à remplir.</span>
              </div>
              <button
                data-testid="profile-open-cta"
                onClick={() => setProfileOpen(true)}
                className="btn-gold-solid h-9 px-4 rounded-sm text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5"
              >
                <UserCog size={13} strokeWidth={1.8} />
                Renseigner
              </button>
            </div>
          )}

          {/* ---- Tabs (Mode selector) ---- */}
          <div
            data-testid="mode-tabs"
            className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3"
          >
            <button
              data-testid="tab-write"
              className="group text-left rounded-md px-5 py-4 border transition-colors"
              style={{
                background: "linear-gradient(to bottom, rgba(212,175,55,0.08), transparent)",
                borderColor: "rgba(212,175,55,0.5)",
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                  style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)" }}
                >
                  <Sparkles size={16} strokeWidth={1.6} style={{ color: "#D4AF37" }} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Mode actif</div>
                  <div className="font-serif-display text-lg text-zinc-100 leading-tight">Écrire une lettre</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Décrivez votre demande, Zerux rédige.</div>
                </div>
              </div>
            </button>

            <button
              data-testid="tab-analyze"
              onClick={() => setCoffreOpen(true)}
              className="group text-left rounded-md px-5 py-4 border border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/[0.03] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <Vault size={16} strokeWidth={1.6} className="text-zinc-400 group-hover:text-[#D4AF37] transition-colors" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 group-hover:text-[#D4AF37] transition-colors">Cliquer pour ouvrir</div>
                  <div className="font-serif-display text-lg text-zinc-100 leading-tight">Mon Coffre</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Uploadez la photo ou le PDF reçu.</div>
                </div>
              </div>
            </button>

            <button
              data-testid="tab-profile"
              onClick={() => setProfileOpen(true)}
              className="group text-left rounded-md px-5 py-4 border border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/[0.03] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <UserCog size={16} strokeWidth={1.6} className="text-zinc-400 group-hover:text-[#D4AF37] transition-colors" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 group-hover:text-[#D4AF37] transition-colors">
                      {profileComplete ? "Enregistrées" : "À renseigner"}
                    </div>
                    {profileComplete && (
                      <span
                        className="text-[8px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-sm"
                        style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37" }}
                      >
                        OK
                      </span>
                    )}
                  </div>
                  <div className="font-serif-display text-lg text-zinc-100 leading-tight">Mes coordonnées</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Nom, adresse, contact — injectés dans chaque lettre.</div>
                </div>
              </div>
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <ChatPane
              messages={messages}
              input={input}
              setInput={setInput}
              onSend={handleSend}
              onClear={handleClear}
              isLoading={isLoading}
            />
            <DocumentPane
              letter={letter}
              isLoading={isLoading}
              onCopy={handleCopy}
              onDownload={handleDownload}
            />
          </div>
        </>
        )}
      </main>

      <Footer />
      <CoffreModal
        open={coffreOpen}
        onClose={() => setCoffreOpen(false)}
        onLetterGenerated={(generatedLetter, summary) => {
          setLetter(generatedLetter);
          if (generatedLetter) saveAsReady(generatedLetter);
          setMessages((m) => [
            ...m,
            {
              role: "bot",
              content:
                "📄 Document analysé. Votre lettre de réponse est prête à droite — également disponible dans Mon Coffre, section « Lettre prête »." +
                (summary ? `\n\nRésumé du document :\n${summary}` : ""),
            },
          ]);
          setCoffreOpen(false);
          toast.success("Lettre générée à partir du document.");
        }}
      />
      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onSaved={refreshProfileState}
      />
    </div>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/bienvenue" element={<Bienvenue />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
      </Routes>
    </BrowserRouter>
  );
}
